import { c as _c } from "react/compiler-runtime";
import { TreeView as TreeViewPrimitive } from '@ark-ui/react/tree-view';
import { cn } from 'cn';
import { ChevronRightIcon, FileIcon, FolderIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
type Node = {
  id: string;
  name: string;
  children?: Node[];
};
type TreeViewProps = React.ComponentProps<typeof TreeViewPrimitive.Root<Node>> & {
  label?: string;
};
export function TreeView(props) {
  const $ = _c(23);
  const t = useTranslations();
  let className;
  let collection;
  let labelProp;
  let rest;
  if ($[0] !== props) {
    ({
      collection,
      className,
      label: labelProp,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = collection;
    $[3] = labelProp;
    $[4] = rest;
  } else {
    className = $[1];
    collection = $[2];
    labelProp = $[3];
    rest = $[4];
  }
  let t0;
  if ($[5] !== labelProp || $[6] !== t) {
    t0 = labelProp ?? t("common.aria-labels.tree-view");
    $[5] = labelProp;
    $[6] = t;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  const label = t0;
  let t1;
  if ($[8] !== className) {
    t1 = cn("rounded-2xl border bg-card p-4", className);
    $[8] = className;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== label) {
    t2 = <TreeViewPrimitive.Label className="sr-only">{label}</TreeViewPrimitive.Label>;
    $[10] = label;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== collection.rootNode.children) {
    t3 = collection.rootNode.children?.map(_temp);
    $[12] = collection.rootNode.children;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== label || $[15] !== t3) {
    t4 = <TreeViewPrimitive.Tree aria-label={label}>{t3}</TreeViewPrimitive.Tree>;
    $[14] = label;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== collection || $[18] !== rest || $[19] !== t1 || $[20] !== t2 || $[21] !== t4) {
    t5 = <TreeViewPrimitive.Root data-slot="tree-view" collection={collection} className={t1} {...rest}>{t2}{t4}</TreeViewPrimitive.Root>;
    $[17] = collection;
    $[18] = rest;
    $[19] = t1;
    $[20] = t2;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
}
function _temp(node, index) {
  return <TreeViewNode key={node.id} node={node} indexPath={[index]} />;
}
type TreeViewNodeProps = TreeViewPrimitive.NodeProviderProps<Node>;
export function TreeViewNode(props) {
  const $ = _c(8);
  const {
    node,
    indexPath
  } = props;
  let t0;
  if ($[0] !== indexPath || $[1] !== node.children || $[2] !== node.name) {
    t0 = node.children ? <TreeViewPrimitive.Branch><TreeViewPrimitive.BranchControl className="ml-[calc((var(--depth)-1)*8px)] flex items-center justify-between rounded-xl px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"><TreeViewPrimitive.BranchText className="flex items-center gap-2"><FolderIcon className="size-4" /> {node.name}</TreeViewPrimitive.BranchText><TreeViewPrimitive.BranchIndicator className="data-open:[&>svg]:rotate-90"><ChevronRightIcon className="size-4 transition-transform duration-200" /></TreeViewPrimitive.BranchIndicator></TreeViewPrimitive.BranchControl><TreeViewPrimitive.BranchContent className="overflow-hidden data-open:animate-tree-view-content-down data-closed:animate-tree-view-content-up"><TreeViewPrimitive.BranchIndentGuide />{node.children.map((child, index) => <TreeViewNode key={child.id} node={child} indexPath={[...indexPath, index]} />)}</TreeViewPrimitive.BranchContent></TreeViewPrimitive.Branch> : <TreeViewPrimitive.Item className="relative ml-[calc((var(--depth)-1)*8px)] rounded-xl px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground data-selected:bg-accent"><TreeViewPrimitive.ItemText className="flex items-center gap-2"><FileIcon className="size-4" />{node.name}</TreeViewPrimitive.ItemText></TreeViewPrimitive.Item>;
    $[0] = indexPath;
    $[1] = node.children;
    $[2] = node.name;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== indexPath || $[5] !== node || $[6] !== t0) {
    t1 = <TreeViewPrimitive.NodeProvider data-slot="tree-view-node" key={node.id} node={node} indexPath={indexPath}>{t0}</TreeViewPrimitive.NodeProvider>;
    $[4] = indexPath;
    $[5] = node;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
export type { Node };
export { createTreeCollection } from '@ark-ui/react/tree-view';